import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/jenkins/service-1.png",
    title: "Pipeline Engineering",
    description:
      "Design scripted and declarative Jenkins pipelines covering build, test, security scans, packaging, and deployment workflows across multi-environment releases.",
  },
  {
    logo_images: "../assets/icons/jenkins/service-2.png",
    title: "Multi-Branch Pipelines",
    description:
      "Automate per-branch pipelines with shared libraries, quality gates, and environment promotion strategies to support trunk-based development and GitFlow models.",
  },
  {
    logo_images: "../assets/icons/jenkins/service-3.png",
    title: "Agent & Infrastructure Management",
    description:
      "Provision and manage Jenkins controller and agent fleets (Kubernetes, Docker, EC2, Windows) with auto-scaling, hardened images, and centralized credential stores.",
  },
  {
    logo_images: "../assets/icons/jenkins/service-4.png",
    title: "Plugin & Integration Strategy",
    description:
      "Integrate Jenkins with SCM, artifact repositories, cloud services, notification platforms, and security tooling using curated plugin baselines and governance.",
  },
  {
    logo_images: "../assets/icons/jenkins/service-5.png",
    title: "Jenkins as Code",
    description:
      "Implement Jenkins Configuration as Code (JCasC) and shared libraries for repeatable controller provisioning, policy enforcement, and disaster recovery.",
  },
];

const benefits = [
  "Unified CI/CD automation across diverse technology stacks",
  "Accelerated release cycles with automated quality and security gates",
  "Consistent governance via Jenkins-as-code and shared libraries",
  "Improved scalability with containerized or cloud-based build agents",
  "Deep visibility through pipeline analytics and audit trails",
  "Reduced manual intervention via standardized delivery workflows",
];

const expertise = [
  "Jenkins Certified Engineers and CloudBees experts",
  "Experience modernizing legacy Jenkins controllers to cloud-native stacks",
  "Shared library development for enterprise-wide reuse",
  "Integration with Kubernetes, Docker, Terraform, and service meshes",
  "Automated compliance workflows with policy enforcement and approvals",
  "Observability and alerting for pipelines, agents, and controllers",
];

const useCases = [
  {
    title: "Global Build Platform",
    description:
      "Modernized on-prem Jenkins to Kubernetes-based controllers with dynamic agents, enabling 5,000+ builds per day and reducing queue times by 70%.",
  },
  {
    title: "Multi-Cloud Delivery",
    description:
      "Delivered multi-branch pipelines deploying to AWS, Azure, and GCP with environment-specific approvals, automated rollbacks, and real-time reporting.",
  },
  {
    title: "Security-Driven CI/CD",
    description:
      "Embedded Snyk, SonarQube, and container scanning into Jenkins pipelines, ensuring every release meets security SLAs before promotion.",
  },
  {
    title: "Shared Services Automation",
    description:
      "Built a Jenkins-as-a-Service offering with JCasC, single sign-on, and centralized plugin lifecycle management for 40 internal product teams.",
  },
];

const reasons = [
  "Blueprints for controller hardening, HA, and DR",
  "Plugin governance and lifecycle automation to minimize risk",
  "Close partnership with security and compliance teams",
  "Enablement programs, documentation, and training for DevOps teams",
  "Flexible support models (managed Jenkins, co-delivery, advisory)",
  "Outcome-driven engagements aligned to release KPIs",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const JenkinsAutomation = () => (
  <>
    <Helmet>
      <meta
        name="description"
        content="Ayanshtech Solutions — Jenkins automation services for pipelines, multi-branch workflows, agent management, and DevOps integrations."
      />
      <meta name="copyright" content="codeglim" />
      <meta
        property="og:title"
        content="Jenkins Automation | CI/CD Pipeline Services | Ayanshtech Solutions"
      />
      <meta property="og:site_name" content="Ayanshtech Solutions" />
      <meta
        property="og:url"
        content="http://ayanshtechnology.com/devops-jenkins"
      />
      <meta
        property="og:description"
        content="Jenkins automation for pipelines, agents, integrations, and CI/CD governance."
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="http://ayanshtechnology.com/assets/logo.png"
      />
      <link rel="canonical" href="http://ayanshtechnology.com/devops-jenkins" />
      <title>
        Jenkins Automation | CI/CD Pipeline Services | Ayanshtech Solutions
      </title>
    </Helmet>

    <div className="flex flex-col items-center justify-center mb-6 p-6">
      <section className="py-5 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
          <div className="lg:w-3/4 lg:text-left p-4">
            <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Jenkins Automation
            </h1>
            <p className="sm:text-base md:text-lg lg:text-lg mb-3">
              Build resilient CI/CD platforms with Jenkins pipelines engineered
              for scale, security, and developer productivity. We modernize
              Jenkins estates with shared libraries, dynamic agents, and
              enterprise integrations.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
            >
              Discuss Jenkins Modernization
            </button>
          </div>
          <div className="flex justify-center mb-12 lg:mb-0">
            <img
              src="../assets/images/devops-pages/Jenkins-banner.png"
              alt="Jenkins Automation"
              className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* <section className="mb-5 mt-10 p-4 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          About Jenkins CI/CD
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Jenkins remains the most extensible automation server, powering
          everything from monolithic builds to cloud-native microservice
          releases. With the right architecture and governance, Jenkins delivers
          consistent pipelines, multi-branch automation, and deep ecosystem
          integrations.
        </p>
      </section> */}

      <section className="text-center mb-5 mt-12 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Jenkins Services
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Comprehensive services covering pipeline engineering, agent
          orchestration, and controller governance.
        </p>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"

            >
              <div className="flex items-center gap-2">
                <div className="w-[60px] h-[60px]  ">
                  <img src={service.logo_images} className="w-full" alt="" />
                </div>
                <h3 className="text-lg font-bold">{service.title}</h3>
              </div>
              <p className="text-lg text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-20 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Benefits of Jenkins Automation
        </h2>
      </section>
      <div className="p-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full rounded-3xl mt-10 ">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Jenkins Expertise
          </h2>
        </div>
        <div className="flex justify-center flex-col md:flex-row item-center">
        <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl my-auto space-y-3 text-gray-700">
          {expertise.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="w-full md:w-1/3 lg:w-[40%]">
          <img src="../assets/images/devops-pages/jenkins-image.png" alt="" />
        </div>
        </div>
      </section>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Use Cases
        </h2>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {useCases.map((useCase) => (
            <div
              key={useCase.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"

            >
              <h3 className="text-lg font-bold text-gray-900">
                {useCase.title}
              </h3>
              <p className="text-lg text-gray-700">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Ayanshtech
        </h2>
      </section>
      <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{reason}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={scrollToContact}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Modernize Your Jenkins Pipelines
          </button>
        </div>
      </div>

      <div id="contact-section" className="w-full mt-12">
        <ContactUs />
      </div>
    </div>
  </>
);

export default JenkinsAutomation;

